import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  ApplySessionPricesSuccessResponseDto,
  ApplySubscriptionPricesSuccessResponseDto,
  BasketClient,
  BlockTooltipResponseDto,
  EventSessionPlanBlockViewDetailsResponseDto,
  EventSessionPlanContingentImageResponseDto,
  EventSessionPlanFilterRequestDto,
  EventSessionPlanImageResponseDto,
  EventSessionPlanLetterResponseDto,
  EventSessionPlanNumberedBlockFilterResponseDto,
  EventSessionPlanResponseDto,
  EventSessionPlanShapeResponseDto,
  EventSessionSalesPlanSeatDetailsResponseDto,
  FileResponse,
  OperationResultOfBasketSessionSeatStatusResponseDto,
  OperationResultOfBookUnBookSeatsWithSubscriptionResponseDto,
  PlanRequestDto,
  SalesPlanBlockFilterRequestDto,
  SalesPlanBlockFilterResponseDto,
  SalesPlanBlockResponseDto,
  SalesPlanFiltersCategoryResponseDto,
  SalesPlanFiltersContingentResponseDto,
  SalesPlanFiltersPriceResponseDto,
  SalesPlanLegendResponseDto,
  SeatPriceApplyingRequestDto,
  SeatsBlockGroupResponseDto,
  SessionSeatsRequestDto,
  StampedVersionSeatsResponseDto,
  SubscriptionPlanBlockDetailsResponseDto,
  SubscriptionPlanResponseDto,
  SubscriptionTopViewBackgroundResponseDto,
  TooltipSeatInfoResponseDto,
} from '@core/services/apiclient';
import { Store, select } from '@ngrx/store';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { SalesEventTypeEnum } from '@sales/models/sales.model';
import { Observable, of, switchMap, take } from 'rxjs';
import { SalesPlanAbstractService } from './sales-plan-abstract.service';
import { SalesPlanEventService } from './sales-plan-event.service';
import { SalesPlanSubscriptionService } from './sales-plan-subscription.service';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanFactoryService implements SalesPlanAbstractService {
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly basketClient = inject(BasketClient);
  private readonly salesPlanService = inject(SalesPlanEventService);
  private readonly salesPlanSubscriptionService = inject(SalesPlanSubscriptionService);

  _type: SalesEventTypeEnum;
  private _service: SalesPlanEventService | SalesPlanSubscriptionService;
  /**
   * a factory that chooses which service to call on runtime,
   * angular providers using @useFactory can't be used here because the factory only executes on module load
   */ public get type() {
    return this._type;
  }

  public set type(_: SalesEventTypeEnum) {
    this._type = _;
  }

  public cancelBasket() {
    return this.store.pipe(
      take(1),
      select(fromSalesPlan.selectors.selectBasketId),
      switchMap((basketId) =>
        basketId > 0 ? this.basketClient.cancel(basketId, Constants.ApiVersion) : of(false),
      ),
    );
  }

  public useFactory(type: SalesEventTypeEnum) {
    this._type = type;
    switch (this._type) {
      case SalesEventTypeEnum.Subscription:
        this._service = this.salesPlanSubscriptionService;
        break;
      case SalesEventTypeEnum.Event:
      default:
        this._service = this.salesPlanService;
        break;
    }
  }

  getPlanBackground?(
    sessionOrSubscriptionId: number,
  ): Observable<SubscriptionTopViewBackgroundResponseDto | FileResponse | null> {
    return this._service.getPlanBackground(sessionOrSubscriptionId);
  }

  getTopviewPlanBlocks?(
    sessionOrSubscriptionId: number,
    blockId: number,
    filters: EventSessionPlanFilterRequestDto | PlanRequestDto,
  ): Observable<EventSessionPlanResponseDto | SubscriptionPlanResponseDto> {
    return this._service.getTopviewPlanBlocks(sessionOrSubscriptionId, blockId, filters);
  }

  getTopviewPlanOnlyBlocks?(
    sessionOrSubscriptionId: number,
    blockIdOrchannelId: number,
    filters?: PlanRequestDto,
  ): Observable<SalesPlanBlockResponseDto[]> {
    return this._service.getTopviewPlanOnlyBlocks(sessionOrSubscriptionId, blockIdOrchannelId, filters);
  }

  getBlockData?(
    sessionOrSubscriptionId: number,
    blockId: number,
    filters: EventSessionPlanFilterRequestDto | PlanRequestDto,
  ): Observable<EventSessionPlanBlockViewDetailsResponseDto | SubscriptionPlanBlockDetailsResponseDto> {
    return this._service.getBlockData(sessionOrSubscriptionId, blockId, filters);
  }

  getNumberedPlanFilter?(
    sessionOrSubscriptionId: number,
    filter: EventSessionPlanFilterRequestDto,
  ): Observable<EventSessionPlanNumberedBlockFilterResponseDto> {
    return this._service.getNumberedPlanFilter(sessionOrSubscriptionId, filter);
  }

  getTootltipSeatInfo?(
    sessionOrSubscriptionId: number,
    seatId: number,
    channelId?: number,
  ): Observable<TooltipSeatInfoResponseDto | EventSessionSalesPlanSeatDetailsResponseDto> {
    return this._service.getTootltipSeatInfo(sessionOrSubscriptionId, seatId, channelId);
  }

  getBlockDetails?(
    channelId: number,
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<BlockTooltipResponseDto> {
    return this._service.getBlockDetails(channelId, sessionOrSubscriptionId, blockId);
  }

  flagSeats?(
    seatIds: number[],
    sessionOrSubscriptionId: number,
    basketId: number,
  ): Observable<
    | OperationResultOfBasketSessionSeatStatusResponseDto
    | OperationResultOfBookUnBookSeatsWithSubscriptionResponseDto
  > {
    return this._service.flagSeats(seatIds, sessionOrSubscriptionId, basketId);
  }

  applyPrices?(
    basketId: number,
    sessionOrSubscriptionId: number,
    seatPriceApplyings: SeatPriceApplyingRequestDto[],
  ): Observable<ApplySessionPricesSuccessResponseDto | ApplySubscriptionPricesSuccessResponseDto> {
    return this._service.applyPrices(basketId, sessionOrSubscriptionId, seatPriceApplyings);
  }

  getSearchResults?(
    sessionOrSubscriptionId: number,
    blockId: number,
    searchTerms: string,
  ): Observable<SeatsBlockGroupResponseDto[]> {
    return this._service.getSearchResults(sessionOrSubscriptionId, blockId, searchTerms);
  }

  flagUnnumberedSeats(
    sessionOrSubscriptionId: number,
    basketId: number,
    sessionSeats: SessionSeatsRequestDto[],
  ): Observable<OperationResultOfBasketSessionSeatStatusResponseDto> {
    return this._service.flagUnnumberedSeats(sessionOrSubscriptionId, basketId, sessionSeats);
  }
  public getLengendInformation(
    salesChannelId: number,
    sessionOrSubscriptionId: number,
  ): Observable<SalesPlanLegendResponseDto> {
    return this._service.getLengendInformation(salesChannelId, sessionOrSubscriptionId);
  }
  getPricesList(
    sessionOrSubscriptionId: number,
    channelId: number,
    contingentIds: number[],
    categoryIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersPriceResponseDto[]> {
    return this._service.getPricesList(
      sessionOrSubscriptionId,
      channelId,
      contingentIds,
      categoryIds,
      blockId,
    );
  }

  getContingentsList(
    sessionOrSubscriptionId: number,
    channelId: number,
    categoryIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersContingentResponseDto[]> {
    return this._service.getContingentsList(
      sessionOrSubscriptionId,
      channelId,
      categoryIds,
      priceIds,
      blockId,
    );
  }
  getCategoriesList(
    sessionOrSubscriptionId: number,
    channelId: number,
    contingentIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersCategoryResponseDto[]> {
    return this._service.getCategoriesList(
      sessionOrSubscriptionId,
      channelId,
      contingentIds,
      priceIds,
      blockId,
    );
  }
  applyTopviewFilter(
    channelId: number,
    sessionOrSubscriptionId: number,
    filters: SalesPlanBlockFilterRequestDto,
  ): Observable<SalesPlanBlockFilterResponseDto[]> {
    return this._service.applyTopviewFilter(channelId, sessionOrSubscriptionId, filters);
  }
  getBlockViewSeats(
    channelId: number,
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this._service.getBlockViewSeats(channelId, sessionOrSubscriptionId, blockId);
  }
  getSalesExcludingMultiBlocksSeats(
    channelId: number,
    sessionOrSubscriptionId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this._service.getSalesExcludingMultiBlocksSeats(channelId, sessionOrSubscriptionId);
  }
  getSalesBlockViewLetters(
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return this._service.getSalesBlockViewLetters(sessionOrSubscriptionId, blockId);
  }
  getDifferentialBlockViewSeats(
    salesChannelId: number,
    eventSessionId: number,
    blockId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this._service.getDifferentialBlockViewSeats(salesChannelId, eventSessionId, blockId, stampVersion);
  }
  getDifferentialSalesExcludingMultiBlocksSeats(
    salesChannelId: number,
    eventSessionId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this._service.getDifferentialSalesExcludingMultiBlocksSeats(
      salesChannelId,
      eventSessionId,
      stampVersion,
    );
  }
  getSalesBlockViewContingentsImages(
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this._service.getSalesBlockViewContingentsImages(sessionOrSubscriptionId, blockId);
  }
  getSalesBlockViewImages(
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanImageResponseDto> {
    return this._service.getSalesBlockViewImages(sessionOrSubscriptionId, blockId);
  }
  getSalesBlockViewShapes(
    sessionOrSubscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return this._service.getSalesBlockViewShapes(sessionOrSubscriptionId, blockId);
  }
  getSalesExcludingMultiBlocksLetters(
    sessionOrSubscriptionId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return this._service.getSalesExcludingMultiBlocksLetters(sessionOrSubscriptionId);
  }
  getSalesExcludingMultiBlocksImages(
    sessionOrSubscriptionId: number,
  ): Observable<EventSessionPlanImageResponseDto> {
    return this._service.getSalesExcludingMultiBlocksImages(sessionOrSubscriptionId);
  }
  getSalesExcludingMultiBlocksContingentsImages(
    sessionOrSubscriptionId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this._service.getSalesExcludingMultiBlocksContingentsImages(sessionOrSubscriptionId);
  }
  getSalesExcludingMultiBlocksShapes(
    sessionOrSubscriptionId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return this._service.getSalesExcludingMultiBlocksShapes(sessionOrSubscriptionId);
  }
}
